"use client";

import type { SupDataGridColumn, SupSingleVaultV3 } from "@/types";
import React from "react";

import GridEmptyState from "@/components/Grid/EmptyState";
import VaultScanTable from "@/components/Vault/Table";

import useApplyCategoryFilter from "@/hooks/data/filters/useApplyCategoryFilter";
import useFilterVaults from "@/hooks/data/useFilterVaults";

import dynamic from "next/dynamic";
import { twMerge } from "tailwind-merge";

const GridBarPanel = dynamic(() => import("@/components/Grid/Bar/Panel"), { ssr: false, loading: () => <div className="sup-filter-bar" /> });

const columns = [
  {
    field: "favorite",
    name: "Favorite",
    hiddenByDefault: true,
  },
  {
    field: "id",
    name: "Vault ID",
    hiddenByDefault: true,
  },
  {
    field: "protocol",
    name: "Protocol",
    hiddenByDefault: true,
  },
  {
    name: "Yield Type",
    field: "yield_type",
  },
  {
    name: "Vault Status",
    field: "is_authenticated",
    hiddenByDefault: true,
  },
  {
    name: "Protocol Status",
    field: "is_authenticated_protocol",
    hiddenByDefault: true,
  },
  {
    name: "24H APY",
    field: "apy_day",
  },
  {
    name: "Rewards",
    field: "rewards",
  },
  {
    name: "7D APY",
    field: "apy_week",
  },
  {
    name: "30D APY",
    field: "apy",
  },
  {
    name: "Sharpe",
    field: "sharpe",
    hiddenByDefault: true,
  },
  {
    name: "Availability",
    field: "availability",
    hiddenByDefault: true,
  },
  {
    name: "List date",
    field: "created_at",
    hiddenByDefault: true,
  },
] as SupDataGridColumn[];

interface VaultScanProps {
  filterVaults?: SupSingleVaultV3[] | string[];
  hideColumns?: string[];
  className?: string;
  allowCategoryFilter?: boolean;
}

export default function VaultMain({ filterVaults = undefined, hideColumns, className, allowCategoryFilter }: VaultScanProps) {
  useApplyCategoryFilter(allowCategoryFilter);

  const { vaults, isLoading, isError, isFiltered } = useFilterVaults(filterVaults);
  const preparedColumns = columns.filter(singleColumn => !hideColumns?.includes(singleColumn.field));

  return (
    <section className={twMerge("flex min-h-0 flex-1 flex-col", className)}>
      <GridBarPanel gridName="Vaults" isDisabled={isLoading || isError} columns={preparedColumns} />
      <div className="max-w-full grow overflow-auto xl:overflow-hidden">
        <div className="flex h-full min-w-full max-w-full grow items-stretch align-middle">
          {isError && <GridEmptyState message="api returned an error" isError />}
          {!isError && <VaultScanTable hideColumns={hideColumns} vaults={vaults} isFiltered={isFiltered} isLoading={isLoading} controlledColumns={columns} />}
        </div>
      </div>
    </section>
  );
}
